import { at, countBy, isNil, omit } from 'lodash-es';

import { downloadUrlA } from '@iconik-io/core/src/download_url_a';
import type { RequiredBy } from '@iconik-io/core/src/types';
import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import {
  type AssetsSegmentSchemaWithIdPatched,
  type AssetsSegmentSchemaWithIdWithRequiredTranscriptionIdPatched,
  asSegment,
  asSegments,
} from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import {
  type AssetsSegmentElasticSchemaWithId,
  type AssetsSegmentElasticSchemaWithIdNullable,
  type AssetsSegmentSchema,
  httpDeleteAssetsByAssetIdSegmentsBySegmentId,
  httpGetAssetsByAssetIdSegments,
  httpGetAssetsByAssetIdSegmentsCsv,
  httpGetAssetsByAssetIdSegmentsSrt,
  httpGetAssetsByAssetIdSegmentsVtt,
  httpPatchAssetsByAssetIdSegmentsBySegmentId,
  httpPatchAssetsByAssetIdVersionsByVersionIdTranscriptionsByTranscriptionIdProperties,
  httpPostAssetsByAssetIdSegments,
  httpPostAssetsByAssetIdVersionsByVersionIdTranscriptionsSubtitles,
} from '@iconik-io/iconik-api/src/services/assets';
import { httpPostTranscribeAssetsByAssetIdProfilesDefault } from '@iconik-io/iconik-api/src/services/transcode';
import type { SpeakerLabels } from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/types';
import { createEntityDenormalizer } from '@iconik-io/shared-lib/src/redux/entities/normalizers';
import {
  deleteNormalizedEntity,
  updateEntities,
} from '@iconik-io/shared-lib/src/redux/entities/slice';
import { normalizeEntities } from '@iconik-io/shared-lib/src/redux/entities/utils';
import { rangeToHmsString } from '@iconik-io/shared-lib/src/utils/range_to_hms_string';

import { WORD_COUNT_DEFAULT } from '../../components/modals/transcriptions_edit_subtitles/transcriptions_edit_subtitles';
import { TRANSCRIPTION_DOWNLOADS_WORDS_PER_LINE } from '../../constants/local_storage';
import {
  selectAllWords,
  selectTranscriptionPartsAndSpeakerLabels,
} from '../../panels/asset_transcriptions/selectors';
import { getCurrentRouteParams } from '../../router/current_route_params';
import type { AppDispatch, GetState } from '../../store/types';
import { errorMessage, successMessage } from '../messages/thunks';
import {
  selectAsset,
  selectAssetProxyTimecodeOptions,
} from '../pages/asset/selectors';
import { selectCurrentVersionId } from '../version/selectors';
import { selectSelectedTranscriptionPropertiesIdByAssetId } from './selectors';

export const upsertTranscriptionSegments =
  (transcriptions: Partial<AssetsSegmentElasticSchemaWithId>[]) =>
  (dispatch: AppDispatch): void => {
    const { entities } = normalizeEntities(
      asSegments(transcriptions),
      'segments',
    );
    dispatch(updateEntities(entities));
  };

const PARAMS = {
  segment_type: 'TRANSCRIPTION',
  sort: 'time_start_milliseconds,asc',
  per_page: 500,
};

export const getTranscriptionSegments =
  (assetId: string, versionId?: string, transcriptionPropertiesId?: string) =>
  (dispatch: AppDispatch) => {
    // Fetch segments in a loop
    let segments: AssetsSegmentElasticSchemaWithIdNullable[] = [];
    const loopFetch = (
      response: EndpointResponse<
        ReturnType<typeof httpGetAssetsByAssetIdSegments>
      >,
    ): Promise<AssetsSegmentElasticSchemaWithIdNullable[]> => {
      const { objects, scroll_id, total } = response.body;
      if (objects) {
        segments = segments.concat(objects);
      }
      if (segments.length < total) {
        return dispatch(
          httpGetAssetsByAssetIdSegments(assetId, {
            ...PARAMS,
            scroll: true,
            scroll_id,
            transcription_id: transcriptionPropertiesId,
          }),
        ).then(loopFetch);
      }

      return Promise.resolve(segments);
    };

    return dispatch(
      httpGetAssetsByAssetIdSegments(assetId, {
        ...PARAMS,
        scroll: true,
        version_id: versionId,
        transcription_id: transcriptionPropertiesId,
      }),
    ).then((response) => loopFetch(response));
  };

export const editTranscriptionSegmentById =
  (
    segmentId: string,
    data: AssetsSegmentSchemaWithIdWithRequiredTranscriptionIdPatched,
  ) =>
  (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    const { assetId } = getState().pages.asset;
    if (!assetId) {
      return Promise.resolve();
    }
    return dispatch(
      httpPatchAssetsByAssetIdSegmentsBySegmentId(assetId, segmentId, data, {
        index_immediately: true,
      }),
    );
  };

export const updateTranscriptionWord =
  (segmentId: string, wordIndex: number, value: string, score: number) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const segment = getState().entities.segments[segmentId];
    if (!segment?.transcription || !segment?.transcription_id) {
      return;
    }

    const words = segment.transcription.words.map((word, index) =>
      wordIndex === index ? { ...word, value, score } : word,
    );
    const updatedSegment: AssetsSegmentSchemaWithIdWithRequiredTranscriptionIdPatched =
      {
        transcription_id: segment.transcription_id,
        face_bounding_boxes: [], // TODO: We need to set bounding boxes not to be null, otherwise the API will return an error. To be fixed in the API.
        transcription: {
          ...segment.transcription,
          words,
        },
        segment_text: words.map((word) => word.value).join(' '),
      };
    dispatch(editTranscriptionSegmentById(segmentId, updatedSegment));
  };

export const transcribeAsset =
  (assetId: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(
      httpPostTranscribeAssetsByAssetIdProfilesDefault(assetId, {}),
    ).then(() => {
      dispatch(successMessage({ text: 'Asset transcription is scheduled' }));
    });
  };

export const deleteTranscriptionSegmentById =
  (segmentId: string) =>
  (dispatch: AppDispatch): void => {
    const assetId = getCurrentRouteParams().id;
    if (!assetId) {
      return;
    }
    dispatch(httpDeleteAssetsByAssetIdSegmentsBySegmentId(assetId, segmentId));
    dispatch(deleteNormalizedEntity({ key: 'segments', id: segmentId }));
  };

export const updateTranscriptionSegmentsSpeaker =
  (segmentIds: string[], speakerId: number) =>
  (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    const { asset } = getState().pages.asset;
    const segments = at(getState().entities.segments, segmentIds);
    const updatedEntities: AssetsSegmentSchemaWithIdWithRequiredTranscriptionIdPatched[] =
      [];
    segments.forEach((segment) => {
      if (segment.transcription && segment.transcription_id) {
        updatedEntities.push({
          id: segment.id,
          transcription_id: segment.transcription_id,
          face_bounding_boxes: [], // TODO: We need to set bounding boxes not to be null, otherwise the API will return an error. To be fixed in the API.
          transcription: {
            words: segment.transcription.words,
            speaker: speakerId,
          },
        });
      }
    });

    const promises: Promise<unknown>[] = updatedEntities.map((segment) =>
      segment.id
        ? dispatch(
            httpPatchAssetsByAssetIdSegmentsBySegmentId(
              asset.id,
              segment.id,
              omit(segment, ['id']),
              {
                index_immediately: true,
              },
            ),
          )
        : Promise.resolve(),
    );
    return Promise.all(promises);
  };

type SplitSegment = {
  segment: AssetsSegmentSchemaWithIdPatched;
  atIndex: number;
};

type UpdateSpeakerSplit = {
  speakerId: number;
  // Which part of the split should be updated to a new speaker ID
  part: 'left' | 'right';
};

type UpdateSegmentResult = {
  action: 'patch' | 'post';
  insertAfter?: string;
  segment: AssetsSegmentSchemaWithIdPatched;
};

const splitUpdateSegmentSpeaker =
  (split: SplitSegment, speaker: UpdateSpeakerSplit) =>
  (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<[UpdateSegmentResult, UpdateSegmentResult]> => {
    const state = getState();
    const { asset } = state.pages.asset;
    const {
      id,
      segment_color,
      segment_checked,
      parent_id,
      drawing,
      ...segmentData
    } = split.segment;
    if (!segmentData.transcription || !segmentData.transcription_id) {
      return Promise.reject();
    }

    const segmentWords = segmentData.transcription.words;
    const firstNewWord = segmentWords[split.atIndex];

    const existingWords = segmentWords.slice(0, split.atIndex);
    const lastExistingWord = existingWords[existingWords.length - 1];

    const patchSegment: AssetsSegmentSchemaWithIdWithRequiredTranscriptionIdPatched =
      {
        transcription_id: segmentData.transcription_id,
        face_bounding_boxes: [], // TODO: We need to set bounding boxes not to be null, otherwise the API will return an error. To be fixed in the API.
        transcription: {
          ...segmentData.transcription,
          speaker:
            speaker.part === 'left'
              ? speaker.speakerId
              : segmentData.transcription.speaker,
          words: existingWords,
        },
        segment_text: existingWords.map((word) => word.value).join(' '),
        time_end_milliseconds:
          (segmentData.time_start_milliseconds ?? 0) + lastExistingWord.end_ms,
      };

    const newWords = segmentWords.slice(split.atIndex).map((word) => ({
      ...word,
      start_ms: word.start_ms - firstNewWord.start_ms,
      end_ms: word.end_ms - firstNewWord.start_ms,
    }));

    const newSegment: RequiredBy<AssetsSegmentSchema, 'transcription_id'> = {
      ...segmentData,
      transcription_id: segmentData.transcription_id,
      face_bounding_boxes: [], // TODO: We need to set bounding boxes not to be null, otherwise the API will return an error. To be fixed in the API.
      transcription: {
        ...segmentData.transcription,
        speaker:
          speaker.part === 'right'
            ? speaker.speakerId
            : segmentData.transcription.speaker,
        words: newWords,
      },
      segment_text: newWords.map((word) => word.value).join(' '),
      time_start_milliseconds:
        (segmentData.time_start_milliseconds ?? 0) + firstNewWord.start_ms,
    };
    if (!newSegment.segment_type) {
      return Promise.reject();
    }
    return Promise.all([
      dispatch(
        httpPatchAssetsByAssetIdSegmentsBySegmentId(
          asset.id,
          split.segment.id,
          patchSegment,
          {
            index_immediately: true,
          },
        ),
      ).then((response) => {
        const result: UpdateSegmentResult = {
          action: 'patch',
          segment: asSegment(response.body),
        };
        return result;
      }),
      dispatch(httpPostAssetsByAssetIdSegments(asset.id, newSegment)).then(
        (response) => {
          const result: UpdateSegmentResult = {
            action: 'post',
            insertAfter: split.segment.id,
            segment: asSegment(response.body),
          };
          return result;
        },
      ),
    ]);
  };

const denormalizeSegment = createEntityDenormalizer('segments');

export const updateTranscriptionWordsSpeaker =
  (wordIds: string[], speakerId: number) =>
  (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    if (wordIds.length === 0) {
      return Promise.resolve();
    }
    const promises: Promise<UpdateSegmentResult | UpdateSegmentResult[]>[] = [];

    const state = getState();
    const { asset } = state.pages.asset;
    const segments = state.entities.segments;
    const { allWords, allWordsIndex } = selectAllWords(
      state,
      getCurrentRouteParams().id,
    );

    const words = at(allWords, at(allWordsIndex, wordIds));

    // Update fully selected segments
    const segmentSelectedWordsCount = countBy(words, (word) => word.segmentId);
    const segmentIds = Object.entries(segmentSelectedWordsCount)
      .filter(([segmentId, selectedCount]) => {
        const segmentWordsLength =
          segments[segmentId]?.transcription?.words.length ?? 0;
        const fullySelected = selectedCount === segmentWordsLength;
        return fullySelected;
      })
      .map(([segmentId]) => segmentId);

    segmentIds.forEach((segmentId) => {
      const segment = segments[segmentId];
      if (!segment.transcription || !segment.transcription_id) {
        return;
      }
      promises.push(
        dispatch(
          httpPatchAssetsByAssetIdSegmentsBySegmentId(
            asset.id,
            segmentId,
            {
              transcription_id: segment.transcription_id,
              face_bounding_boxes: [], // TODO: We need to set bounding boxes not to be null, otherwise the API will return an error. To be fixed in the API.
              transcription: {
                ...segment.transcription,
                speaker: speakerId,
              },
            },
            {
              index_immediately: true,
            },
          ),
        ).then((response) => {
          const result: UpdateSegmentResult = {
            action: 'patch',
            segment: asSegment(response.body),
          };
          return result;
        }),
      );
    });

    // Split first and last selected segment if necessary
    let splitSegmentStart: SplitSegment | undefined;
    const firstWord = words[0];
    const startSegmentId = firstWord.segmentId;
    const startSegment = denormalizeSegment(
      getState().entities,
      startSegmentId,
    );
    const startSegmentLength = startSegment?.transcription?.words.length ?? 0;
    const startSelectedLength = segmentSelectedWordsCount[startSegmentId];
    if (startSegment && startSelectedLength < startSegmentLength) {
      splitSegmentStart = {
        segment: startSegment,
        atIndex: firstWord.localIndex,
      };
    }

    let splitSegmentEnd: SplitSegment | undefined;
    const lastWord = words[words.length - 1];
    const endSegmentId = lastWord.segmentId;
    const endSegment = denormalizeSegment(getState().entities, endSegmentId);
    const endSegmentLength = endSegment?.transcription?.words.length ?? 0;
    const endSelectedLength = segmentSelectedWordsCount[endSegmentId];
    if (endSegment && endSelectedLength < endSegmentLength) {
      splitSegmentEnd = {
        segment: endSegment,
        atIndex: lastWord.localIndex + 1,
      };
    }

    if (
      splitSegmentStart &&
      startSegmentId === endSegmentId &&
      splitSegmentStart.atIndex > 0
    ) {
      // Selection is in the middle of the segment, we need to split it twice
      promises.push(
        dispatch(
          splitUpdateSegmentSpeaker(splitSegmentStart, {
            speakerId,
            part: 'right',
          }),
        ).then((result) => {
          const prevSpeaker = result[0].segment.transcription?.speaker;
          const newSegmentLength =
            result[1].segment.transcription?.words.length ?? 0;
          if (!isNil(prevSpeaker) && newSegmentLength > words.length) {
            return dispatch(
              splitUpdateSegmentSpeaker(
                {
                  segment: result[1].segment,
                  atIndex: words.length,
                },
                {
                  speakerId: prevSpeaker,
                  part: 'right',
                },
              ),
            ).then((endResult) => [...result, ...endResult]);
          }
          return result;
        }),
      );
    } else {
      if (splitSegmentStart && splitSegmentStart.atIndex > 0) {
        promises.push(
          dispatch(
            splitUpdateSegmentSpeaker(splitSegmentStart, {
              speakerId,
              part: 'right',
            }),
          ),
        );
      }
      if (splitSegmentEnd) {
        promises.push(
          dispatch(
            splitUpdateSegmentSpeaker(splitSegmentEnd, {
              speakerId,
              part: 'left',
            }),
          ),
        );
      }
    }

    return Promise.all(promises);
  };

export const updateTranscriptionSpeakerLabels =
  (assetId: string, speakerLabels: SpeakerLabels) =>
  (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    const transcriptionProperties =
      selectSelectedTranscriptionPropertiesIdByAssetId(getState(), assetId);

    if (!transcriptionProperties) {
      dispatch(errorMessage({ text: "Can't find transcription properties." }));
      return Promise.resolve();
    }

    const { asset_id, version_id, id } = transcriptionProperties;

    if (!asset_id || !version_id) {
      dispatch(errorMessage({ text: 'Asset or version id is not set.' }));
      return Promise.resolve();
    }

    return dispatch(
      httpPatchAssetsByAssetIdVersionsByVersionIdTranscriptionsByTranscriptionIdProperties(
        asset_id,
        version_id,
        id,
        {
          speaker_labels: speakerLabels,
        },
      ),
    );
  };

export const downloadTranscriptionsVtt =
  (assetId: string) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();
    const asset = state.entities.assets[assetId];
    const versionId = selectCurrentVersionId(
      state,
      assetId,
      getCurrentRouteParams().versionId,
    );
    const transcriptionProperties =
      selectSelectedTranscriptionPropertiesIdByAssetId(getState(), assetId);
    if (transcriptionProperties?.id) {
      dispatch(
        httpGetAssetsByAssetIdSegmentsVtt(assetId, {
          segment_type: 'TRANSCRIPTION',
          version_id: versionId,
          transcription_id: transcriptionProperties.id,
          words_per_line: Number(
            localStorage.getItem(TRANSCRIPTION_DOWNLOADS_WORDS_PER_LINE) ||
              WORD_COUNT_DEFAULT,
          ),
        }),
      ).then(({ body }) => {
        const downloadUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(
          body,
        )}`;
        downloadUrlA(downloadUrl, `${asset.title}-transcription.vtt`);
      });
    }
  };

export const downloadTranscriptionsSrt =
  (assetId: string) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();
    const asset = state.entities.assets[assetId];
    const versionId = selectCurrentVersionId(
      state,
      assetId,
      getCurrentRouteParams().versionId,
    );
    const transcriptionProperties =
      selectSelectedTranscriptionPropertiesIdByAssetId(getState(), assetId);
    if (transcriptionProperties?.id) {
      dispatch(
        httpGetAssetsByAssetIdSegmentsSrt(assetId, {
          segment_type: 'TRANSCRIPTION',
          version_id: versionId,
          transcription_id: transcriptionProperties.id,
          words_per_line: Number(
            localStorage.getItem(TRANSCRIPTION_DOWNLOADS_WORDS_PER_LINE) ||
              WORD_COUNT_DEFAULT,
          ),
        }),
      ).then(({ body }) => {
        const downloadUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(
          body,
        )}`;
        downloadUrlA(downloadUrl, `${asset.title}-transcription.srt`);
      });
    }
  };

export const downloadTranscriptionsCsv =
  (assetId: string) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();
    const asset = state.entities.assets[assetId];
    const versionId = selectCurrentVersionId(
      state,
      assetId,
      getCurrentRouteParams().versionId,
    );
    const transcriptionProperties =
      selectSelectedTranscriptionPropertiesIdByAssetId(getState(), assetId);
    if (transcriptionProperties?.id) {
      dispatch(
        httpGetAssetsByAssetIdSegmentsCsv(assetId, {
          segment_type: 'TRANSCRIPTION',
          version_id: versionId,
          transcription_id: transcriptionProperties.id,
        }),
      ).then(({ body }) => {
        const downloadUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(
          // The CSV endpoint answers with the file's text, which the spec
          // declares as no body at all.
          String(body),
        )}`;
        downloadUrlA(downloadUrl, `${asset.title}-transcription.csv`);
      });
    }
  };

export const downloadTranscriptionsTextBySpeaker =
  (assetId: string) =>
  (_dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();
    const asset = selectAsset(state, assetId);
    if (!asset) {
      return;
    }

    const timecodeOptions = selectAssetProxyTimecodeOptions(
      state,
      asset.id,
      getCurrentRouteParams().versionId,
    );

    const { speakerParts, speakerLabels } =
      selectTranscriptionPartsAndSpeakerLabels(state, asset.id);
    let text = '';
    speakerParts.forEach((part) => {
      const segmentsArray = at(state.entities.segments, part.ids);
      if (!segmentsArray.length) {
        return;
      }
      const transcriptions = segmentsArray.map(
        (segment) => segment.segment_text,
      );
      text += !isNil(part.speaker)
        ? speakerLabels[part.speaker]
        : 'Speaker not set';
      const firstSegment = segmentsArray[0];
      const lastSegment = segmentsArray[segmentsArray.length - 1];
      const startMilliseconds =
        firstSegment?.time_start_milliseconds ?? undefined;
      const endMilliseconds = lastSegment?.time_end_milliseconds ?? undefined;
      if (timecodeOptions) {
        const time = rangeToHmsString({
          startMilliseconds,
          endMilliseconds,
          timecodeOptions,
        });
        if (time) {
          text += ` (${time})`;
        }
      }
      text += `\n\n${transcriptions.join(' ')}\n\n\n`;
    });

    const downloadUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(
      text,
    )}`;

    downloadUrlA(downloadUrl, `${asset.title}-transcription.txt`);
  };

export const downloadTranscriptionsTextBySegment =
  (assetId: string) =>
  (_dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();
    const asset = selectAsset(state, assetId);
    if (!asset) {
      return;
    }

    const timecodeOptions = selectAssetProxyTimecodeOptions(
      state,
      asset.id,
      getCurrentRouteParams().versionId,
    );

    const { speakerParts, speakerLabels } =
      selectTranscriptionPartsAndSpeakerLabels(state, asset.id);
    let text = '';
    speakerParts.forEach((part) => {
      const segmentsArray = at(state.entities.segments, part.ids);
      if (!segmentsArray.length) {
        return;
      }
      segmentsArray.forEach((segment) => {
        text += !isNil(part.speaker)
          ? speakerLabels[part.speaker]
          : 'Speaker not set';
        const startMilliseconds = segment.time_start_milliseconds;
        const endMilliseconds = segment.time_end_milliseconds;
        if (timecodeOptions) {
          const time = rangeToHmsString({
            startMilliseconds,
            endMilliseconds,
            timecodeOptions,
          });
          if (time) {
            text += ` (${time})`;
          }
        }
        text += `\n\n${segment.segment_text}\n\n\n`;
      });
    });

    const downloadUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(
      text,
    )}`;

    downloadUrlA(downloadUrl, `${asset.title}-transcription.txt`);
  };

export const transcribeSubtitle =
  ({
    assetId,
    versionId,
    subtitleId,
    overwrite,
  }: {
    assetId: string;
    versionId: string;
    subtitleId: string;
    overwrite: boolean;
  }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    await dispatch(
      httpPostAssetsByAssetIdVersionsByVersionIdTranscriptionsSubtitles(
        assetId,
        versionId,
        {
          source_subtitle_id: subtitleId,
          delete_old_transcriptions: overwrite,
        },
      ),
    );
    dispatch(successMessage({ text: 'Transcribe job scheduled' }));
  };
